import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  savePhaseItem,
  deletePhaseItem,
  requestPhaseItems,
  mergePurchaseOrderToOriginal,
} from 'actionCreators';
import { can } from '../../../../../../../../services';
import { getRole } from '../../../../../../../../reducers';
import LineItemTable from 'components/LineItemTable';
import PropTypes from 'prop-types';
import PurchaseOrderRow from 'purchaseOrderRow';
import PhaseLineItemTables from 'phaseLineItemTables';
import getLineItemTotal from '../../../../../../../../selectors/lineItemTotal';
import { getPhaseLineItemsWithTotal } from '../../../../../../../../selectors/phaseLineItem';
import getPurchaseOrderOptions from '../../../../../../../../selectors/purchaseOrderOptions';
import purchaseOrderShape, { purchaseOrderOptionsType } from 'types/purchaseOrder';
import documentType from 'types/documentUpload';

const LINE_ITEM_TYPE = 'purchaseOrders';

class PurchaseOrderTable extends Component {
  constructor(props) {
    super(props);
    this.state = { poIds: [], isInEditMode: false };
  }

  componentWillMount = () => this.refreshLineItemList();

  componentDidMount = () => (this.props.showLineItems ?
    this.togglePoLineItems(this.getAllItemIds(), true) :
    null);

  componentDidUpdate = (prevProps, prevState) => {
    const { showLineItems, tableName, toggleShowHide, cv } = this.props;
    const { poIds } = this.state;
    const itemIds = this.getAllItemIds();
    if (prevProps !== this.props && showLineItems &&
        (poIds.length !== itemIds.length)) {
      this.togglePoLineItems(itemIds, true);
    }
    if (prevProps.showLineItems !== showLineItems) {
      if (showLineItems) this.togglePoLineItems(itemIds, true);
      if (!showLineItems) this.togglePoLineItems([], true);
    }
    if (prevState.poIds.length !== poIds.length &&
      poIds.length === 0 && !cv) {
      toggleShowHide(tableName, false);
    } else if (prevState.poIds.length !== poIds.length &&
               poIds.length === itemIds.length && !cv) {
      toggleShowHide(tableName, true);
    }
  }

  getAllItemIds = () => {
    const { lineItems } = this.props;
    const newLineItems = [];
    lineItems.forEach((li) => {
      if (li.id > 0) newLineItems.push(li.id);
    });
    return newLineItems;
  }

  handleMergeToOriginal = purchaseOrderId =>
    this.props.mergePurchaseOrderToOriginal(this.props.phaseId, purchaseOrderId);

  refreshLineItemList = () => {
    this.props.requestPhaseItems(this.props.phaseId, LINE_ITEM_TYPE);
    this.props.requestPhaseItems(this.props.phaseId, 'phaseLineItems');
  }

  handleDelete = lineItemId =>
    this.props.deletePhaseItem(this.props.phaseId, { id: lineItemId }, LINE_ITEM_TYPE);

  handleSave = lineItem => this.props.savePhaseItem(this.props.phaseId, lineItem, LINE_ITEM_TYPE);

  determineOriginalExists = (lineItems) => {
    let exists = false;
    lineItems.forEach((lineItem) => {
      if (lineItem.kind === 'Original') exists = true;
    });
    return exists;
  }

  togglePoLineItems = (itemIds, toggleAll = false) => {
    const { poIds } = this.state;
    const newPoIdsArr = poIds.slice();
    if (toggleAll) {
      // add all line items to poIds
      this.setState({ poIds: itemIds });
    } else {
      // add single line Item to poIds
      if (!newPoIdsArr.includes(itemIds)) {
        newPoIdsArr.push(itemIds);
      } else {
        // remove single line item from poIds
        const itemIdInd = newPoIdsArr.indexOf(itemIds);
        newPoIdsArr.splice(itemIdInd, 1);
      }
      this.setState({ poIds: newPoIdsArr });
    }
  }

  toggleEditMode = bool => this.setState({ isInEditMode: bool });

  renderRow = (item, originalExists) => {
    const { documents, lpa, role, purchaseOrderOptions,
            phaseLineItems, expandableRow, projectNumber } = this.props;
    return (<PurchaseOrderRow
      key={item.id}
      projectNumber={projectNumber}
      role={role}
      onSave={this.handleSave}
      onDelete={this.handleDelete}
      lineItem={item}
      originalExists={originalExists}
      documents={documents}
      readOnly={!lpa &&
        !can(role, 'update', 'purchase_order', { obj_status: item.invoice_status })}
      purchaseOrderOptions={purchaseOrderOptions}
      phaseLineItems={phaseLineItems}
      toggleLineItem={this.state.isInEditMode ? null : () => this.togglePoLineItems(item.id)}
      expandableRow={expandableRow}
    />);
  }

  render = () => {
    const { lineItems, total, editable, phaseId, cv, filterLineItems, role,
    } = this.props;
    const { poIds } = this.state;
    const originalExists = this.determineOriginalExists(lineItems);
    return (<div>
      <LineItemTable
        title="Purchase Orders"
        refreshList={this.refreshLineItemList}
        total={total}
        hasCreatableRow={editable}
        readOnly={!editable}
        toggleEditMode={this.toggleEditMode}
        diffChildren={!cv}
        cv={cv}
        accountingTable
      >
        {lineItems.map(item => (cv ? this.renderRow(item, originalExists) :
          (<div key={item.id}>
            {this.renderRow(item, originalExists)}
            {!cv && poIds.includes(item.id) && <PhaseLineItemTables
              poNumber={item.number}
              poId={item.id}
              phaseId={phaseId}
              filterLineItems={filterLineItems}
              onMergeToOriginal={this.handleMergeToOriginal}
              canMergeToOriginal={can(role, 'merge_to_original', 'purchase_order') && originalExists}
            />}
          </div>)
        ))}
      </LineItemTable>
    </div>);
  }
}

PurchaseOrderTable.defaultProps = {
  parentLineItems: [],
  lineItems: [],
  documents: [],
  cv: false,
  showLineItems: false,
  expandableRow: false,
  filterLineItems: false,
};

PurchaseOrderTable.propTypes = {
  projectNumber: PropTypes.string,
  phaseId: PropTypes.string,
  tableName: PropTypes.string,
  toggleShowHide: PropTypes.func,
  requestPhaseItems: PropTypes.func.isRequired,
  savePhaseItem: PropTypes.func.isRequired,
  deletePhaseItem: PropTypes.func.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape(purchaseOrderShape),
  ),
  parentLineItems: PropTypes.arrayOf(
    PropTypes.shape(purchaseOrderShape),
  ),
  documents: PropTypes.arrayOf(documentType).isRequired,
  total: PropTypes.number,
  editable: PropTypes.bool,
  lpa: PropTypes.bool,
  showLineItems: PropTypes.bool,
  role: PropTypes.string,
  purchaseOrderOptions: purchaseOrderOptionsType,
  phaseLineItems: PropTypes.arrayOf(PropTypes.object),
  cv: PropTypes.bool,
  expandableRow: PropTypes.bool,
  filterLineItems: PropTypes.bool,
  mergePurchaseOrderToOriginal: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  projectNumber: state.phase.details.project_number,
  purchaseOrderOptions: getPurchaseOrderOptions(state),
  phaseLineItems: getPhaseLineItemsWithTotal(state),
  lineItems: (ownProps.parentLineItems || []).length > 0 ?
    ownProps.parentLineItems : state.phase.purchaseOrders.items,
  total: getLineItemTotal(state.phase.purchaseOrders.items),
  editable: can(getRole(state), 'editable', 'purchase_order'),
  lpa: can(getRole(state), 'update', 'LPA'),
  role: getRole(state),
});

export default connect(mapStateToProps, {
  savePhaseItem,
  deletePhaseItem,
  requestPhaseItems,
  mergePurchaseOrderToOriginal,
})(PurchaseOrderTable);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAccounting/components/purchaseOrderTable.jsx